<template>
  <div class="tiptap-wrap min-w-0 overflow-hidden rounded-md border border-gray-300 focus-within:ring-1 focus-within:ring-indigo-500">
    <div v-if="editor" class="tiptap-toolbar flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1">
      <button
        type="button" class="tt-btn font-bold" :class="active('bold')"
        title="Pogrubienie (Ctrl+B)" @click="editor.chain().focus().toggleBold().run()"
      >B</button>
      <button
        type="button" class="tt-btn italic" :class="active('italic')"
        title="Kursywa (Ctrl+I)" @click="editor.chain().focus().toggleItalic().run()"
      >I</button>
      <button
        type="button" class="tt-btn underline" :class="active('underline')"
        title="Podkreślenie (Ctrl+U)" @click="editor.chain().focus().toggleUnderline().run()"
      >U</button>
      <button
        type="button" class="tt-btn line-through" :class="active('strike')"
        title="Przekreślenie" @click="editor.chain().focus().toggleStrike().run()"
      >S</button>
      <span class="mx-1 border-l border-gray-300" />
      <label class="tt-color-label" title="Kolor tekstu" @mousedown.prevent>
        <span class="tt-color-swatch" :style="{ backgroundColor: currentTextColor || '#374151' }" aria-hidden="true" />
        <input
          type="color"
          class="tt-color-input"
          :value="currentTextColor || '#374151'"
          @mousedown.prevent
          @input="setTextColor($event.target.value)"
        >
      </label>
      <button
        type="button" class="tt-btn"
        title="Usuń kolor tekstu"
        :disabled="!currentTextColor"
        @click="editor.chain().focus().unsetColor().run()"
      >⨯ Kolor</button>
      <button
        type="button" class="tt-btn font-mono" :class="active('code')"
        title="Kod w linii" @click="editor.chain().focus().toggleCode().run()"
      >&lt;/&gt;</button>
      <span class="mx-1 border-l border-gray-300" />
      <button
        type="button" class="tt-btn" :class="active('paragraph')"
        title="Akapit" @click="editor.chain().focus().setParagraph().run()"
      >P</button>
      <button
        type="button" class="tt-btn" :class="active('heading', { level: 1 })"
        title="Nagłówek 1" @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >H1</button>
      <button
        type="button" class="tt-btn" :class="active('heading', { level: 2 })"
        title="Nagłówek 2" @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >H2</button>
      <button
        type="button" class="tt-btn" :class="active('heading', { level: 3 })"
        title="Nagłówek 3" @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >H3</button>
      <button
        type="button" class="tt-btn" :class="active('heading', { level: 4 })"
        title="Nagłówek 4" @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
      >H4</button>
      <span class="mx-1 border-l border-gray-300" />
      <button
        type="button" class="tt-btn" :class="active('bulletList')"
        title="Lista punktowana" @click="editor.chain().focus().toggleBulletList().run()"
      >• Lista</button>
      <button
        type="button" class="tt-btn" :class="active('orderedList')"
        title="Lista numerowana" @click="editor.chain().focus().toggleOrderedList().run()"
      >1. Lista</button>
      <button
        type="button" class="tt-btn" :class="active('blockquote')"
        title="Cytat" @click="editor.chain().focus().toggleBlockquote().run()"
      >Cytat</button>
      <button
        type="button" class="tt-btn font-mono" :class="active('codeBlock')"
        title="Blok kodu" @click="editor.chain().focus().toggleCodeBlock().run()"
      >{ }</button>
      <button
        type="button" class="tt-btn"
        title="Linia pozioma" @click="editor.chain().focus().setHorizontalRule().run()"
      >―</button>
      <span class="mx-1 border-l border-gray-300" />
      <button
        type="button" class="tt-btn" :class="active('link')"
        title="Wstaw/edytuj link (działa też na zaznaczonym zdjęciu)" @click="setLink"
      >Link</button>
      <button
        type="button" class="tt-btn"
        title="Usuń link" :disabled="!editor.isActive('link')" @click="editor.chain().focus().unsetLink().run()"
      >Usuń link</button>
      <button
        type="button" class="tt-btn"
        title="Wstaw zdjęcie z adresu URL" @click="addImageByUrl"
      >🖼 URL</button>
      <button
        type="button" class="tt-btn" :disabled="uploadingImage"
        title="Wgraj zdjęcie z dysku (PNG, JPG)" @click="pickImageFile"
      >{{ uploadingImage ? '… Wysyłam' : '⬆ Zdjęcie' }}</button>
      <input
        ref="fileInput"
        type="file"
        accept="image/png,image/jpeg"
        class="hidden"
        @change="onImageFileSelected"
      >
      <span class="mx-1 border-l border-gray-300" />
      <!-- Image size & alignment — active only when an image is selected -->
      <button
        v-for="size in IMAGE_SIZES" :key="`size-${size}`"
        type="button" class="tt-btn" :class="imageAttr('size', size)"
        :disabled="!editor.isActive('image')"
        :title="`Szerokość zdjęcia ${size}%`"
        @click="setImageSize(size)"
      >{{ size }}%</button>
      <button
        type="button" class="tt-btn"
        :disabled="!editor.isActive('image')"
        title="Wyrównaj zdjęcie do lewej (tekst opływa z prawej)"
        :class="imageAttr('align', 'left')"
        @click="setImageAlign('left')"
      >⬅</button>
      <button
        type="button" class="tt-btn"
        :disabled="!editor.isActive('image')"
        title="Wyśrodkuj zdjęcie"
        :class="imageAttr('align', 'center')"
        @click="setImageAlign('center')"
      >⬌</button>
      <button
        type="button" class="tt-btn"
        :disabled="!editor.isActive('image')"
        title="Wyrównaj zdjęcie do prawej (tekst opływa z lewej)"
        :class="imageAttr('align', 'right')"
        @click="setImageAlign('right')"
      >➡</button>
      <button
        type="button" class="tt-btn"
        :disabled="!editor.isActive('image')"
        title="Wyczyść rozmiar i wyrównanie zdjęcia"
        @click="resetImageLayout"
      >⨯ Zdjęcie</button>
      <span class="mx-1 border-l border-gray-300" />
      <button
        type="button" class="tt-btn"
        title="Cofnij (Ctrl+Z)" :disabled="!editor.can().chain().focus().undo().run()" @click="editor.chain().focus().undo().run()"
      >↺</button>
      <button
        type="button" class="tt-btn"
        title="Ponów (Ctrl+Y)" :disabled="!editor.can().chain().focus().redo().run()" @click="editor.chain().focus().redo().run()"
      >↻</button>
      <button
        type="button" class="tt-btn"
        title="Wyczyść formatowanie" @click="editor.chain().focus().unsetAllMarks().clearNodes().run()"
      >⨯ Format</button>
    </div>
    <editor-content :editor="editor" class="tiptap-content prose prose-sm max-w-none px-4 py-3" />
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';

const IMAGE_ALIGNS = ['left', 'center', 'right'];
const IMAGE_SIZES = ['25', '50', '75', '100'];

// Image size/alignment are stored as CSS classes (rt-img-*) rather than inline
// styles, because HtmlSanitizer only allows the `class` attribute on <img>
// (not style/width). State is parsed back from those classes on reload. The
// matching CSS lives in styles/application.css so it applies in the editor and
// on the rendered page alike.
const StyledImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      align: {
        default: null,
        parseHTML: (el) => IMAGE_ALIGNS.find((a) => el.classList.contains(`rt-img-${a}`)) || null,
        renderHTML: (attrs) => (attrs.align ? { class: `rt-img-${attrs.align}` } : {}),
      },
      size: {
        default: null,
        parseHTML: (el) => IMAGE_SIZES.find((s) => el.classList.contains(`rt-img-${s}`)) || null,
        renderHTML: (attrs) => (attrs.size ? { class: `rt-img-${attrs.size}` } : {}),
      },
    };
  },
});

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Zacznij pisać...' },
});

const emit = defineEmits(['update:modelValue']);

const editor = useEditor({
  content: props.modelValue || '',
  extensions: [
    // StarterKit v3 already bundles Bold/Italic/Underline/Link/Heading/Lists/
    // Quote/Code/Strike/UndoRedo/Dropcursor/Gapcursor/TrailingNode. We add
    // only Image (not in StarterKit) and Placeholder (lives under
    // @tiptap/extensions in v3).
    StarterKit.configure({
      link: {
        openOnClick: false,
        // TipTap defaults to target="_blank"; we want same-tab navigation.
        HTMLAttributes: { target: null, rel: null },
      },
    }),
    // inline: true lets images sit next to each other (and next to text) within
    // one paragraph, and — being inline nodes — they accept the link mark, so an
    // image can be turned into a link (select it, then use the Link button).
    StyledImage.configure({ inline: true, allowBase64: true }),
    TextStyle,
    Color.configure({ types: ['textStyle'] }),
    Placeholder.configure({ placeholder: props.placeholder }),
  ],
  onUpdate: ({ editor: ed }) => {
    emit('update:modelValue', ed.getHTML());
    syncTextColor(ed);
  },
  onSelectionUpdate: ({ editor: ed }) => {
    syncTextColor(ed);
  },
});

// Keep editor in sync if parent overwrites the model (e.g. form reset).
watch(() => props.modelValue, (val) => {
  if (!editor.value) return;
  if (val === editor.value.getHTML()) return;
  editor.value.commands.setContent(val || '', { emitUpdate: false });
});

onBeforeUnmount(() => editor.value?.destroy());

const currentTextColor = ref('');

function syncTextColor(ed) {
  currentTextColor.value = ed.getAttributes('textStyle').color || '';
}

function setTextColor(color) {
  if (!color || !editor.value) return;
  const chain = editor.value.chain().focus();
  if (!editor.value.state.selection.empty) {
    chain.extendMarkRange('textStyle');
  }
  chain.setColor(color).run();
}

function active(name, attrs) {
  if (!editor.value) return '';
  return editor.value.isActive(name, attrs) ? 'is-active' : '';
}

function setLink() {
  const previousUrl = editor.value.getAttributes('link').href;
  const url = window.prompt('Adres URL', previousUrl || 'https://');
  if (url === null) return;
  if (url === '') {
    editor.value.chain().focus().extendMarkRange('link').unsetLink().run();
    return;
  }
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
}

// Insert an image by URL (https://tiptap.dev/docs/examples/basics/images).
function addImageByUrl() {
  const url = window.prompt('Adres URL zdjęcia', 'https://');
  if (!url) return;
  const alt = window.prompt('Opis zdjęcia (alt)', '') || '';
  editor.value.chain().focus().setImage({ src: url, alt }).run();
}

// Upload an image from disk to the backend (POST /admin/rich-text-images). The
// server validates format/size, downscales oversized images and returns a URL,
// which is inserted as an <img> — no base64 is stored in block content.
const UPLOAD_URL = '/admin/rich-text-images';
const fileInput = ref(null);
const uploadingImage = ref(false);

function pickImageFile() {
  fileInput.value?.click();
}

// Highlights the toolbar button matching the selected image's current
// size/align attribute.
function imageAttr(name, value) {
  if (!editor.value) return '';
  return editor.value.getAttributes('image')[name] === value ? 'is-active' : '';
}

function setImageSize(size) {
  const current = editor.value.getAttributes('image').size;
  editor.value.chain().focus().updateAttributes('image', { size: current === size ? null : size }).run();
}

function setImageAlign(align) {
  const current = editor.value.getAttributes('image').align;
  editor.value.chain().focus().updateAttributes('image', { align: current === align ? null : align }).run();
}

function resetImageLayout() {
  editor.value.chain().focus().updateAttributes('image', { size: null, align: null }).run();
}

function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : '';
}

async function onImageFileSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = ''; // allow re-selecting the same file later
  if (!file) return;

  uploadingImage.value = true;
  try {
    const body = new FormData();
    body.append('file', file);

    const response = await fetch(UPLOAD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-XSRF-TOKEN': getCsrfToken(),
        Accept: 'application/json',
      },
      body,
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Nie udało się wgrać zdjęcia.');
    }

    editor.value.chain().focus().setImage({ src: payload.url, alt: payload.alt || '' }).run();
  } catch (error) {
    window.alert(error.message || 'Nie udało się wgrać zdjęcia.');
  } finally {
    uploadingImage.value = false;
  }
}
</script>

<style scoped>
.tt-btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  cursor: pointer;
}
.tt-btn:hover:not(:disabled) {
  background: #f3f4f6;
}
.tt-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.tt-btn.is-active {
  background: #eef2ff;
  border-color: #6366f1;
  color: #4338ca;
}
.tiptap-content :deep(.ProseMirror) {
  min-height: 16rem;
  outline: none;
  overflow-x: hidden;
}
.tiptap-content :deep(.ProseMirror p.is-editor-empty:first-child::before) {
  color: #9ca3af;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}
.tiptap-content :deep(.ProseMirror h1) {
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 2.25rem;
}
.tiptap-content :deep(.ProseMirror h2) {
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 2rem;
}
.tiptap-content :deep(.ProseMirror h3) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.75rem;
}
.tiptap-content :deep(.ProseMirror h4) {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.625rem;
}
.tt-color-label {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.375rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.25rem;
  background: white;
  cursor: pointer;
}
.tt-color-swatch {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 1px solid #d1d5db;
  border-radius: 0.125rem;
}
.tt-color-input {
  width: 1.5rem;
  height: 1.25rem;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
</style>
